import React, { useCallback, useRef, useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UploadCloud, Link2, Loader2, ImageIcon } from "lucide-react";

const ACCEPT = "image/png,image/jpeg,image/gif,image/webp,image/svg+xml,image/bmp,image/avif";

export default function UploadZone({ onCreated }) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [url, setUrl] = useState("");
  const inputRef = useRef(null);

  const uploadOne = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const res = await api.post("/images/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  };

  const uploadFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter((f) => f && f.type.startsWith("image/"));
      if (files.length === 0) {
        toast.error("Please choose image files");
        return;
      }
      const tooBig = files.filter((f) => f.size > 10 * 1024 * 1024);
      const valid = files.filter((f) => f.size <= 10 * 1024 * 1024);
      if (tooBig.length) toast.error(`${tooBig.length} file(s) skipped — over the 10MB limit`);
      if (valid.length === 0) return;

      setBusy(true);
      let done = 0;
      let failed = 0;
      for (const file of valid) {
        setProgress({ done, total: valid.length });
        try {
          const img = await uploadOne(file);
          done += 1;
          onCreated(img);
        } catch (e) {
          failed += 1;
        }
      }
      setBusy(false);
      setProgress(null);
      if (done > 0) toast.success(`${done} image${done > 1 ? "s" : ""} uploaded — links ready!`);
      if (failed > 0) toast.error(`${failed} upload(s) failed`);
    },
    [onCreated]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(false);
      uploadFiles(e.dataTransfer.files);
    },
    [uploadFiles]
  );

  useEffect(() => {
    const onPaste = (e) => {
      const files = Array.from(e.clipboardData?.items || [])
        .filter((i) => i.type.startsWith("image/"))
        .map((i) => i.getAsFile());
      if (files.length) uploadFiles(files);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [uploadFiles]);

  const submitUrl = async () => {
    const value = url.trim();
    if (!/^https?:\/\//i.test(value)) {
      toast.error("Enter a valid image URL (http/https)");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/images/url", { url: value });
      toast.success("Image linked — share links ready!");
      onCreated(res.data);
      setUrl("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add URL");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tabs defaultValue="upload" className="w-full">
      <TabsList className="grid grid-cols-2 w-full max-w-sm mx-auto mb-6" data-testid="upload-mode-tabs">
        <TabsTrigger value="upload" data-testid="tab-upload">
          <UploadCloud className="h-4 w-4 mr-2" /> Upload file
        </TabsTrigger>
        <TabsTrigger value="url" data-testid="tab-url">
          <Link2 className="h-4 w-4 mr-2" /> Paste URL
        </TabsTrigger>
      </TabsList>

      <TabsContent value="upload">
        <div
          data-testid="upload-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`relative cursor-pointer rounded-2xl border-2 border-dashed transition-all duration-200 p-12 sm:p-16 text-center ${
            dragging
              ? "border-accent bg-accent/5 scale-[1.01]"
              : "border-border hover:border-accent/60 hover:bg-secondary/40"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            hidden
            data-testid="file-input"
            onChange={(e) => uploadFiles(e.target.files)}
          />
          <div className="flex flex-col items-center gap-4">
            <div className={`h-16 w-16 rounded-2xl flex items-center justify-center transition-colors ${dragging ? "bg-accent text-accent-foreground" : "bg-secondary text-foreground"}`}>
              {busy ? <Loader2 className="h-7 w-7 animate-spin" /> : <ImageIcon className="h-7 w-7" />}
            </div>
            <div>
              <p className="font-heading font-semibold text-lg">
                {busy
                  ? progress
                    ? `Uploading ${progress.done + 1} of ${progress.total}…`
                    : "Uploading…"
                  : "Drop images, or click to browse"}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Drop several at once · or paste from clipboard (Ctrl / ⌘ + V)
              </p>
            </div>
            <p className="text-xs text-muted-foreground font-mono">PNG · JPG · GIF · WEBP · SVG — up to 10MB each</p>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="url">
        <div className="rounded-2xl border border-border bg-card p-8 sm:p-10">
          <label className="text-sm font-medium mb-2 block">Image URL</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="url-input"
              placeholder="https://example.com/photo.jpg"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitUrl()}
              className="font-mono text-sm h-12"
            />
            <Button data-testid="url-submit-btn" onClick={submitUrl} disabled={busy} className="h-12 px-6 font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate links"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            We'll create shareable embed codes and a short link for any existing image URL.
          </p>
        </div>
      </TabsContent>
    </Tabs>
  );
}
